import { useCallback, useEffect, useState } from "react";

import {
  maintenanceApi,
  useCancelMaintenanceWindowMutation,
  useGetMaintenanceWindowsQuery,
} from "../api/maintenance.api";
import { MaintenanceCreateDialog } from "../components/MaintenanceCreateDialog";
import { MaintenanceHistoryTable } from "../components/MaintenanceHistoryTable";
import MaintenanceSheet from "../components/MaintenanceSheet";
import { MaintenanceUpdateDialog } from "../components/MaintenanceUpdateDialog";
import { MAINTENANCE_FILTER_KEYS } from "../constants/maintenance.constants";
import { useMaintenanceBypassAccess } from "../hooks/useMaintenanceBypassAccess";
import { maintenanceMessages } from "../maintenance.intl";
import type { MaintenanceHistoryEntry } from "../types/maintenance.types";

import { API_TAGS } from "@/app/constants/apiTags.constants";
import { TWENTY_FIVE } from "@/app/constants/constants";
import { MODULE_CONSTANTS } from "@/app/constants/modules.constant";
import { SOCKET_KEYS } from "@/app/constants/socket.constant";
import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import CustomAlert from "@/components/alerts/CustomAlert";
import { customToast } from "@/components/alerts/CustomToast";
import DataTableSlotContextProvider from "@/components/dataTable/DataTableSlotContext";
import { useTableQueryState } from "@/components/dataTable/hooks/useTableQueryState";
import CustomHeader from "@/components/headers";
import { useSearchPaginationQuery } from "@/components/pagination";
import {
  usePagination,
  type PaginationMeta,
} from "@/components/pagination/hooks/usePagination";
import NoPermissionPage from "@/components/statePages/NoPermissionPage";
import { useGetParams } from "@/hooks/query/useGetParams";
import { useSocketEvents } from "@/hooks/useSocketEvents";
import { SocketNamespaces } from "@/interfaces/globals";
import { validateResponse } from "@/utils/form/formHelper";

const MaintenancePage = (): React.JSX.Element => {
  const canAccessMaintenancePage = useMaintenanceBypassAccess();
  const t = useIntlMessages(maintenanceMessages);

  const [selectedEntry, setSelectedEntry] =
    useState<MaintenanceHistoryEntry | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<
    MaintenanceHistoryEntry | undefined
  >(undefined);

  const { setLocalPagination, queryFilter, shouldSkipQuery, localPagination } =
    useTableQueryState({
      filterKeys: MAINTENANCE_FILTER_KEYS,
    });
  const filter = useGetParams(MAINTENANCE_FILTER_KEYS);

  const queryToSend = useSearchPaginationQuery(filter, queryFilter, {
    filterParams: MAINTENANCE_FILTER_KEYS,
  });

  const { data, isLoading } = useGetMaintenanceWindowsQuery(
    { filters: queryToSend.queryFilter },
    {
      skip: shouldSkipQuery,
    },
  );

  const rows = data?.data ?? [];

  // Cancel dialog state
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelMaintenance, { isLoading: isCancelling }] =
    useCancelMaintenanceWindowMutation();

  const handleOpenAddDialog = useCallback((): void => {
    setIsCreateDialogOpen(true);
  }, []);

  const handleEditEntry = useCallback(
    (entry: MaintenanceHistoryEntry): void => {
      setEditingEntry(entry);
    },
    [],
  );

  const handleCloseCreateDialog = useCallback((): void => {
    setIsCreateDialogOpen(false);
  }, []);

  const handleCloseUpdateDialog = useCallback((): void => {
    setEditingEntry(undefined);
  }, []);

  const handleViewEntry = useCallback(
    (id: string): void => {
      const entry = rows.find((row) => row.id === id) ?? null;

      setSelectedEntry(entry);
      setIsDetailSheetOpen(true);
    },
    [rows],
  );

  const handleCloseSheet = useCallback((): void => {
    setIsDetailSheetOpen(false);
    setSelectedEntry(null);
  }, []);

  useEffect(() => {
    if (!isDetailSheetOpen || !selectedEntry) {
      return;
    }

    const updatedEntry = rows.find((row) => row.id === selectedEntry.id);

    if (updatedEntry && updatedEntry !== selectedEntry) {
      setSelectedEntry(updatedEntry);
    }
  }, [rows, isDetailSheetOpen, selectedEntry]);

  const handleConfirmCancel = useCallback(async (): Promise<void> => {
    if (!cancelId) {
      return;
    }

    const res = await cancelMaintenance({ id: cancelId });
    validateResponse({
      response: res,
      onSuccess: (message) => {
        customToast.success(message ?? t("maintenanceCancelled"));
        setCancelId(null);
      },
    });
  }, [cancelId, cancelMaintenance, t]);

  const pagination = usePagination(
    data?.pagination as PaginationMeta,
    TWENTY_FIVE,
    setLocalPagination,
    localPagination,
  );

  useSocketEvents(SocketNamespaces.BASE, [
    {
      events: SOCKET_KEYS.MAINTENANCE_WINDOW,
      invalidations: [
        {
          api: maintenanceApi,
          tags: [API_TAGS.MAINTENANCE.CURRENT, API_TAGS.MAINTENANCE.WINDOWS],
          moduleName: MODULE_CONSTANTS.MAINTENANCE,
        },
      ],
    },
  ]);

  if (!canAccessMaintenancePage) {
    return <NoPermissionPage />;
  }

  return (
    <DataTableSlotContextProvider>
      <section>
        <CustomHeader
          title={t("pageTitle")}
          btnProps={{
            btnName: t("createMaintenance"),
            showBtn: true,
            handleBtnClick: handleOpenAddDialog,
            icon: "add",
            variant: "default",
          }}
        />

        <div className='mt-8'>
          <MaintenanceHistoryTable
            rows={rows}
            pagination={pagination}
            isLoading={isLoading}
            onViewEntry={handleViewEntry}
            onEditEntry={handleEditEntry}
            onCancelEntry={setCancelId}
          />
        </div>

        <MaintenanceSheet
          isOpen={isDetailSheetOpen}
          toggleOpen={handleCloseSheet}
          entry={selectedEntry}
          onEditEntry={handleEditEntry}
          onCancelEntry={setCancelId}
        />

        <MaintenanceCreateDialog
          isOpen={isCreateDialogOpen}
          onClose={handleCloseCreateDialog}
          onSuccess={handleCloseCreateDialog}
        />

        <MaintenanceUpdateDialog
          isOpen={editingEntry !== undefined}
          maintenanceEntry={editingEntry}
          onClose={handleCloseUpdateDialog}
          onSuccess={handleCloseUpdateDialog}
        />

        <CustomAlert
          isOpen={cancelId !== null}
          setIsOpen={(open) => {
            if (!open) {
              setCancelId(null);
            }
          }}
          title={t("cancelConfirmTitle")}
          desc={t("cancelConfirmDescription")}
          btnName={t("abort")}
          cancelName={t("goBack")}
          btnAction={handleConfirmCancel}
          isLoading={isCancelling}
        />
      </section>
    </DataTableSlotContextProvider>
  );
};

export default MaintenancePage;
